import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../../data'
import { Panel } from '../primitives'
import { problemOf } from './format'

/**
 * The one-time switch: the rules on the network server today become "other
 * network rules", carried unchanged, and from then on Kronos pushes. Nothing is
 * pushed before an admin has read what is there and pressed the button — and if
 * the rules change while they read, the button refuses rather than adopting
 * something nobody saw.
 */
export function Takeover(): JSX.Element {
  const queryClient = useQueryClient()
  const [problem, setProblem] = useState('')
  const preview = useQuery({ queryKey: ['access-takeover'], queryFn: () => repository.takeoverPreview() })
  const take = useMutation({
    mutationFn: (seen: string) => repository.takeOver(seen),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['connectivity'] }),
    onError: (e: Error) => {
      setProblem(problemOf(e))
      void preview.refetch()
    },
  })

  return (
    <div className="page">
      <Panel padded>
        <div className="stack">
          <h3 className="access-card__name">Manage network access from Kronos</h3>
          <p>
            Today the network server’s rules are edited by hand. Taking over keeps every rule below exactly as it is —
            office networks, routes, groups — and from then on Kronos adds who may SSH into which machine, and is the
            only place that changes it. Edits made directly on the network server are put back within a minute.
          </p>
          {preview.isPending ? <p className="muted">Reading the current rules…</p> : null}
          {preview.isError ? <p className="critical">Could not reach the network server.</p> : null}
          {preview.data?.problem ? <p className="critical">{preview.data.problem}</p> : null}
          {preview.data?.live ? (
            <>
              <span className="field-label">The rules today</span>
              <pre className="access-rules">{preview.data.live}</pre>
            </>
          ) : null}
          {problem ? <p className="critical" role="alert">{problem}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              className="primary"
              disabled={!preview.data?.live || !!preview.data.problem || take.isPending}
              onClick={() => preview.data?.live && take.mutate(preview.data.live)}
            >
              Take over
            </button>
          </div>
        </div>
      </Panel>
    </div>
  )
}
